// The cost report's lines by user, task group and task, in tabs, each sortable by any number; a
// group or task links to its page. User lines carry no name, so the users list names them.
import { Link } from "react-router";

import { shortId } from "../pages/format";
import { type Column, DataTable } from "../ui/DataTable";
import { TabPanel, Tabs } from "../ui/Tabs";
import { type Messages, useT } from "../i18n";
import { compact, type CostLine, type CostReport, money, tokens } from "./model";

export type LineKind = "users" | "groups" | "tasks";

interface Names {
  user: (id: string) => string;
  /** A task line's group, from the report's group lines. */
  group: (id: string) => string | undefined;
}

type Copy = Messages["costs"];

function nameColumn(t: Copy, kind: LineKind, names: Names): Column<CostLine> {
  switch (kind) {
    case "users":
      return { key: "name", header: t.user, sortValue: (l) => names.user(l.id).toLowerCase(), cell: (l) => <span className="font-semibold text-gray-900">{names.user(l.id)}</span> };
    case "groups":
      return {
        key: "name",
        header: t.taskGroup,
        sortValue: (l) => l.name.toLowerCase(),
        cell: (l) => (
          <div className="min-w-0">
            <Link to={`/task-groups/${l.id}`} className="block max-w-[22rem] truncate font-semibold text-gray-900 hover:text-primary">
              {l.name}
            </Link>
            {l.user_id ? <span className="text-xs text-gray-500">{names.user(l.user_id)}</span> : null}
          </div>
        ),
      };
    case "tasks":
      return {
        key: "name",
        header: t.task,
        sortValue: (l) => l.name.toLowerCase(),
        cell: (l) => (
          <div className="min-w-0">
            <Link to={`/tasks/${l.id}`} className="block max-w-[22rem] truncate font-semibold text-gray-900 hover:text-primary">
              {l.title ?? l.name}
            </Link>
            {l.task_group_id ? <span className="text-xs text-gray-500">{names.group(l.task_group_id) ?? shortId(l.task_group_id)}</span> : null}
          </div>
        ),
      };
  }
}

const num = (key: string, header: string, value: (l: CostLine) => number, show: (n: number) => string, hideBelow?: Column<CostLine>["hideBelow"]): Column<CostLine> => ({
  key,
  header,
  hideBelow,
  className: "text-end",
  sortValue: value,
  cell: (l) => <span className="tabular whitespace-nowrap">{show(value(l))}</span>,
});

function columns(t: Copy, kind: LineKind, names: Names): Column<CostLine>[] {
  return [
    nameColumn(t, kind, names),
    num("cost", t.cost, (l) => l.cost_usd, money),
    num("org", t.usersKey, (l) => l.org_cost_usd, money, "lg"),
    num("platform", t.platformKey, (l) => l.platform_cost_usd, money, "lg"),
    num("tokens", t.tokens, tokens, compact, "md"),
    num("steps", t.steps, (l) => l.steps, String, "sm"),
  ];
}

const KINDS: LineKind[] = ["users", "groups", "tasks"];

export function CostTables({ report, kind, onKind, userName }: { report: CostReport; kind: LineKind; onKind: (k: LineKind) => void; userName: (id: string) => string }) {
  const t = useT().costs;
  const groups = new Map(report.groups.map((g) => [g.id, g.name]));
  const names: Names = { user: userName, group: (id) => groups.get(id) };
  const rows = report[kind];
  return (
    <div>
      <Tabs
        label={t.costLines}
        tabs={KINDS.map((k) => ({
          key: k,
          label: t.tabs[k],
          badge: <span className="tabular rounded-full bg-gray-100 px-1.5 text-[11px] text-gray-600">{report[k].length}</span>,
        }))}
        value={kind}
        onChange={onKind}
        className="mb-3"
      />
      <TabPanel id={kind}>
        {rows.length === 0 ? (
          <p className="m-0 py-6 text-center text-[13px] text-gray-500">{t.emptyPeriod}</p>
        ) : (
          <DataTable key={kind} label={t.tabs[kind]} columns={columns(t, kind, names)} rows={rows} rowKey={(l) => l.id} defaultSort={{ key: "cost", dir: "desc" }} />
        )}
      </TabPanel>
    </div>
  );
}
